import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { usePrefetchIntent } from '../../hooks/usePrefetchIntent'
import { bottomNavItems, entryPath, isPathActive } from '../../lib/nav-items'
import { useNavSettings } from '../../queries/navSettings'

interface Props {
	onMoreClick: () => void
}

const TAB_BASE =
	'relative flex-1 flex flex-col items-center gap-1 py-2.5 px-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome-foreground/80 focus-visible:ring-inset'

function tabClass(isActive: boolean): string {
	return `${TAB_BASE} ${
		isActive
			? 'text-chrome-foreground before:absolute before:top-0 before:inset-x-4 before:h-0.5 before:rounded-full before:bg-chrome-foreground'
			: 'text-chrome-foreground/80 hover:text-chrome-foreground'
	}`
}

export function MobileBottomNav({ onMoreClick }: Props) {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { user, hasPermission } = useAuth()
	const intent = usePrefetchIntent()
	const navSettings = useNavSettings(user?.id ?? '')

	const bottomItems = bottomNavItems(hasPermission, navSettings)
	const isOnBottomItem = bottomItems.some((item) => isPathActive(pathname, item.path))

	return (
		<nav className="md:hidden print:hidden fixed bottom-0 left-0 right-0 bg-chrome pb-safe z-30" aria-label="Bottom navigation">
			<div className="flex">
				{bottomItems.map((item) => {
					const isActive = isPathActive(pathname, item.path)
					return (
						<button
							key={item.path}
							{...intent(entryPath(item, hasPermission))}
							onClick={() => navigate(entryPath(item, hasPermission))}
							aria-current={isActive ? 'page' : undefined}
							className={tabClass(isActive)}
						>
							<i className={`ti ${item.icon} text-xl leading-none`} aria-hidden="true" />
							<span className="text-[10px] leading-none font-medium">{item.label}</span>
						</button>
					)
				})}
				<button onClick={onMoreClick} className={tabClass(!isOnBottomItem)} aria-label="More navigation options">
					<i className="ti ti-dots text-xl leading-none" aria-hidden="true" />
					<span className="text-[10px] leading-none font-medium">More</span>
				</button>
			</div>
		</nav>
	)
}
